import { useEffect, useState, useCallback, useRef } from 'react';
import { getHealthUrl } from '@/config/apiConfig';

/** While the screen is hidden, one health check per this interval. */
const ONLINE_INTERVAL_MS = 15 * 1000;
/** While the connecting screen is up, keep checking until the network returns. */
const OFFLINE_INTERVAL_MS = 2 * 1000;
const PING_TIMEOUT_MS = 8000;
/** Consecutive failed checks before the connecting screen is shown. */
const FAILURES_BEFORE_SHOW = 3;

/**
 * True when this client app should show the fullscreen "connecting" overlay.
 * - Only where `enabled`: the surface can('offlineScreen') (kiosk and mobile, src/config/surfacePolicy.ts).
 * - Every check calls GET {VITE_API_URL}/health. navigator.onLine is ignored: a stuck
 *   offline flag must not keep the screen up after the API answers.
 * - Screen hidden: check every 15s. The screen appears after 3 consecutive failures.
 *   A 2xx response resets that count.
 * - Screen up: check every 2s. One 2xx response hides it and returns to the 15s cadence.
 *
 * Reachability uses the app backend, not a third-party URL like gstatic — otherwise the
 * overlay can stick after reconnect when Google is blocked but the API is reachable.
 */
export function useKioskOfflineScreen(enabled: boolean): {
  show: boolean;
  retry: () => Promise<void>;
  retryBusy: boolean;
} {
  const [show, setShow] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  const showRef = useRef(false);
  const failuresRef = useRef(0);
  const generationRef = useRef(0);
  const inFlightGenerationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const runCheck = useCallback(async (generation: number) => {
    if (!enabled || generation !== generationRef.current) return;
    if (inFlightGenerationRef.current === generation) return;
    inFlightGenerationRef.current = generation;

    const finish = (reachable: boolean) => {
      if (generation !== generationRef.current) return;
      if (reachable) {
        failuresRef.current = 0;
        if (showRef.current) {
          showRef.current = false;
          setShow(false);
        }
        return;
      }
      failuresRef.current += 1;
      if (failuresRef.current >= FAILURES_BEFORE_SHOW && !showRef.current) {
        showRef.current = true;
        setShow(true);
      }
    };

    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const timeoutId = window.setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
      try {
        const res = await fetch(`${getHealthUrl()}?kiosk=${Date.now()}`, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
          credentials: 'omit',
        });
        if (!res.ok) {
          throw new Error(`health ${res.status}`);
        }
        finish(true);
      } catch {
        finish(false);
      } finally {
        window.clearTimeout(timeoutId);
        if (abortRef.current === controller) abortRef.current = null;
      }
    } finally {
      if (inFlightGenerationRef.current === generation) {
        inFlightGenerationRef.current = 0;
      }
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      failuresRef.current = 0;
      showRef.current = false;
      setShow(false);
      return;
    }

    const generation = ++generationRef.current;
    let timer = 0;
    let cancelled = false;

    const arm = () => {
      if (cancelled) return;
      const delay = showRef.current ? OFFLINE_INTERVAL_MS : ONLINE_INTERVAL_MS;
      timer = window.setTimeout(() => {
        void tick();
      }, delay);
    };

    const tick = async () => {
      await runCheck(generation);
      if (cancelled || generation !== generationRef.current) return;
      arm();
    };

    void tick();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      generationRef.current += 1;
      abortRef.current?.abort();
    };
  }, [enabled, runCheck]);

  const retry = useCallback(async () => {
    if (!enabled) return;
    setRetryBusy(true);
    try {
      await runCheck(generationRef.current);
    } finally {
      setRetryBusy(false);
    }
  }, [enabled, runCheck]);

  if (!enabled) {
    return { show: false, retry, retryBusy: false };
  }

  return { show, retry, retryBusy };
}
